import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import type { RestaurantWithEventSpaces } from "@/types/restaurant";
import { RestaurantImage } from "./restaurant-card/RestaurantImage";
import { EventTypesList } from "./restaurant-card/EventTypesList";
import { CardHeader } from "./restaurant-card/CardHeader";
import { SpacesList } from "./restaurant-card/SpacesList";

interface RestaurantCardProps {
  restaurant: RestaurantWithEventSpaces;
}

export const RestaurantCard = ({ restaurant }: RestaurantCardProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { session } = useAuth();
  const { toggleFavorite, isFavorited } = useFavorites();

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.spaces-trigger') || 
        (e.target as HTMLElement).closest('.favorite-button')) {
      e.stopPropagation();
      if ((e.target as HTMLElement).closest('.spaces-trigger')) {
        setIsOpen(!isOpen);
      }
    } else {
      navigate(`/restaurant/${restaurant.id}`);
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!session) {
      navigate('/auth');
      return;
    }
    toggleFavorite(restaurant.id);
  };

  return (
    <Card 
      className="overflow-hidden transition-all duration-300 hover:translate-y-[-4px] hover:shadow-lg cursor-pointer group relative bg-white"
      onClick={handleClick}
    >
      <Button
        variant="ghost"
        size="icon"
        className="favorite-button absolute top-3 right-3 z-10 bg-white/90 hover:bg-white shadow-sm hover:shadow-md transition-all"
        onClick={handleFavoriteClick}
      >
        <Heart 
          className={`h-4 w-4 ${isFavorited(restaurant.id) ? 'fill-red-500 text-red-500' : 'text-gray-500'}`}
        />
      </Button>
      <RestaurantImage restaurant={restaurant} />
      <CardHeader restaurant={restaurant} />
      <div className="px-4 pb-4">
        <SpacesList 
          restaurant={restaurant}
          isOpen={isOpen}
          onOpenChange={setIsOpen}
        />
        <EventTypesList 
          eventTypes={restaurant.eventspaces?.[0]?.event_types} 
          maxVisible={3}
        />
      </div>
    </Card>
  );
};